/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type DocNode, type TextDefinition } from '@atlaskit/adf-schema';
// eslint-disable-next-line no-unused-vars
import { cssMap, cx, jsx } from '@atlaskit/css';
import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { type EditorActions } from '@atlaskit/editor-core/actions';
import { ComposableEditor, type EditorNextProps } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { WithEditorActions } from '@atlaskit/editor-core/WithEditorActions';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useFlags } from '@atlaskit/flag';
import EditIcon from '@atlaskit/icon/core/edit';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { useSubscribe } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
// Note: `@atlassian/ai-audio` is intentionally NOT imported here. It is
// loaded lazily via `<VoiceEnabledFooterRow>` so the
// audio-dictation chunk is excluded from the chat-input critical path.
import { useMentionAgentSource } from '@atlassian/conversation-assistant-agent/controllers/mention-agents/use-mention-agent-source';
import type { MentionAgent } from '@atlassian/conversation-assistant-agent/services/mention-agents/types';
import { useAutoSuggestTracking } from '@atlassian/conversation-assistant-auto-suggest/autosuggest-tracking-store';
import { calculateEditDistances } from '@atlassian/conversation-assistant-auto-suggest/calculate-edit-distance';
import { extractContent } from '@atlassian/conversation-assistant-auto-suggest/content-extraction';
import {
	MAX_CHARACTERS_INPUT,
	useEditorPreset,
} from '@atlassian/conversation-assistant-content-renderer/controllers/editor';
import { normalizeConnectorSourceDoc } from '@atlassian/conversation-assistant-content-renderer/controllers/editor/connector-source-input-rule';
import { useRovoMentionProvider } from '@atlassian/conversation-assistant-content-renderer/utils/mention-provider';
import { encodePromptToAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import {
	createSkillAdf,
	ROVO_SKILL_COMBINED_EXTENSION_KEY,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { type SkillSelectionSource } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import { useDraftsController } from '@atlassian/conversation-assistant-store/controllers/drafts';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { useRovoUserSettings } from '@atlassian/conversation-assistant-store/controllers/rovo-user-settings';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { useHighlightActionsEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useHighlightActionsEditorPlaceholder';
import { validUrls } from '@atlassian/conversation-assistant-ui-components/utils/url-validation';
import {
	isDefaultRovoChat,
	normalizeAccountIdFromIdentityAri,
} from '@atlassian/conversation-assistant-utils/default-agent';
import { useAutoApplyAvailability } from '@atlassian/creation-settings/auto-apply';
import { AutoApplyToggleMenuItem } from '@atlassian/creation-settings/auto-apply-toggle-menu-item';
import { messages as autoApplyToggleMessages } from '@atlassian/creation-settings/messages';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import type { Skill } from '@atlassian/rovo-platform-types/skills';

import { useConversationExternalAttachments } from '../../common/controllers/conversation-external-attachments';
import { useConversationFiles } from '../../common/controllers/conversation-files';
import { useChatMentionResource } from '../../common/hooks/useChatMentionResource';
import { useLinkTypeAhead } from '../../common/hooks/useLinkTypeAhead';
import { useRenderStabilizedCallback } from '../../common/hooks/useRenderStabilizedCallback';
import { useRovoTtaiSegmentRenderStabilizationEnabled } from '../../common/hooks/useRovoTtaiSegmentRenderStabilizationEnabled';
import { useUnconnectedSourceTip } from '../../common/hooks/useUnconnectedSourceTip';
import { useUrlInsertion } from '../../common/hooks/useUrlInsertion';
import { type MentionAnalyticsEvent } from '../../common/services/rovo-chat-mention-resource/mention-analytics-event';
import { UseCase } from '../../common/ui/chat-input-action-buttons/current-page-as-context/UseCase';
import { usePageContextType } from '../../common/ui/chat-input-action-buttons/current-page-as-context/usePageContextType';
import { getActualTextContentLength } from '../../common/utils/node/getActualTextContentLength';
import { useAppConnectionBannerActions } from '../../controllers/app-connection-banner-store/useAppConnectionBannerActions';
import { useAppConnectionBannerStore } from '../../controllers/app-connection-banner-store/useAppConnectionBannerStore';
import { useChatModeStore } from '../../controllers/chat-mode-store/useChatModeStore';
import { useChatModeStoreActions } from '../../controllers/chat-mode-store/useChatModeStoreActions';
import { useDeactivatedSelectedAgentIds } from '../../controllers/selected-agent-drafts/useDeactivatedSelectedAgentIds';
import { useWebSearchConfiguration } from '../../controllers/web-search-configuration';

import { sendAgentMentionDismissedAnalyticsEvent } from './agent-mention-analytics/agent-mention-dismissed-analytics';
import { sendAgentMentionSubmittedAnalyticsEvent } from './agent-mention-analytics/agent-mention-submitted-analytics';
import { sendMentionMenuAnalyticsEvent } from './agent-mention-analytics/mention-menu-analytics';
import { AppConnectionBanner } from './app-connection-banner';
import { areRovoControlsSuppressed } from './areRovoControlsSuppressed';
import { useViewMoreModalParamsEffect } from './chat-mode-menu/app-filter/useViewMoreModalParamsEffect';
import { ChatModeToggleMenuItem } from './chat-mode-menu/ChatModeToggleMenuItem';
import { IconContainer } from './chat-mode-menu/IconContainer';
import { messages as chatModeMessages } from './chat-mode-menu/messages';
import { ChatInputInner, type AutoSuggestOption } from './ChatInputInner';
import { ConnectorTip } from './connector-source-connection-tip/ConnectorTip';
import { DictationMicPermissionTip } from './connector-source-connection-tip/DictationMicPermissionTip';
import { ContextBanner } from './context-banner';
import { useActiveContext } from './context-banner/useActiveContext';
import { type ControlPanelProps } from './control-panel/types';
import { getSourceModeForSubmit } from './getSourceModeForSubmit';
import { getWebSearchEnabledForSubmit } from './getWebSearchEnabledForSubmit';
import { MentionAgentsSpotlightController } from './MentionAgentsSpotlightController';
import messages from './messages';
import { SkillsSpotlightController } from './SkillsSpotlightController';
import { SpaceContextBanner } from './space-context-banner';
import { getAppFiltersForSubmit } from './submit-payload-helpers';
import type { ChatInputInternalProps, ChatPromptInputProps, ChatPromptInputRef } from './types';
import { useAgentRoutingIdByIdentityAccountId } from './useAgentRoutingIdByIdentityAccountId';
import { useBoundaryActionsMenuItemsVisibility } from './useBoundaryActionsMenuItemsVisibility';
import { useBoundaryAssistanceServiceParams } from './useBoundaryAssistanceServiceParams';
import { useIsSpeechToTextEnabled } from './useIsSpeechToTextEnabled';
import { usePromptRouting } from './usePromptRouting';
import { useReconcileSelectedAgentsWithEditorMentions } from './useReconcileSelectedAgentsWithEditorMentions';
import { useSelectedAgentTransition } from './useSelectedAgentTransition';

const WRAPPER_BORDER_RADIUS = token('radius.xlarge');
const WRAPPER_INLINE_MARGIN = token('space.200');

const DEFAULT_INPUT_HEIGHT_ROVO_CHAT = 56;
// 2 lines height + 8px padding applied in ComposableEditor
const DEFAULT_INPUT_HEIGHT_STAGING_AREA = 48;
const BUTTON_TOOLTIP_OPTIONS: ControlPanelProps['tooltipOptions'] = {
	position: 'top',
};

const styles = cssMap({
	wrapperMarginInline: {
		marginInline: WRAPPER_INLINE_MARGIN,
	},

	wrapperMarginBlock: {
		marginBlockStart: token('space.200'),
	},

	fsCentered: {
		width: 'min(800px, 95vw)',
		marginInline: 'auto',
	},

	relativeWrapper: {
		position: 'relative',
	},

	dropdownSlotAnchor: {
		position: 'relative',
		height: '0px',
	},

	dropdownSlotOverlay: {
		position: 'absolute',
		insetBlockStart: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
		zIndex: 100,
	},
	// Elevates the prompt above the outerBottomAccessory (z-index 1 > 0) so its
	// opaque background covers the overlap where the two surfaces meet, leaving
	// only the prompt's border-radius corner gaps visible against the sunken tray.
	promptInputLayerAboveBottomAccessory: {
		position: 'relative',
		zIndex: 1,
	},

	outerBottomAccessory: {
		position: 'relative',
		zIndex: 0,
		backgroundColor: token('elevation.surface.sunken'),
		marginInline: WRAPPER_INLINE_MARGIN,
		// marginBlockStart is negative to account for the border radius of the prompt
		// marginBlockStart offsets the difference between paddingBlockStart and paddingBlockEnd.
		// if you make a change, it should remain paddingBlockStart - marginBlockStart = paddingBlockEnd
		marginBlockStart: token('space.negative.200'),
		paddingBlockStart: token('space.300'),
		paddingBlockEnd: token('space.100'),
		paddingInline: token('space.100'),
		borderStartStartRadius: 0,
		borderStartEndRadius: 0,
		borderEndStartRadius: WRAPPER_BORDER_RADIUS,
		borderEndEndRadius: WRAPPER_BORDER_RADIUS,
	},
});

const createInitialPromptTextDocument = (initialPromptText: string): DocNode =>
	encodePromptToAdf(initialPromptText, undefined, true).adf;

/**
 * Wrapper component for the AppConnectionBanner that connects to the store.
 * Displays a banner prompting users to connect to apps required by a selected prompt.
 * Only shown when the prompt library v2 experiment is on.
 */
const AppConnectionBannerWrapper = (): React.JSX.Element | null => {
	const [{ unconnectedApps, isVisible, forceMultiline, showConnectedState }] =
		useAppConnectionBannerStore();
	const { hideBanner, markAppAsConnected } = useAppConnectionBannerActions();

	if (!isVisible || unconnectedApps.length === 0) {
		return null;
	}

	// The 3P suggested-prompts variant renders inline in the chat thread
	// (InThreadAppConnectionPrompt), not in the input area — keeping it independent
	// of the rovo_chat_prompt_library_v2_exp gate that guards the shared banner.
	if (showConnectedState) {
		return null;
	}

	if (!FeatureGates.getExperimentValue('rovo_chat_prompt_library_v2_exp', 'isEnabled', false)) {
		return null;
	}

	return (
		<AppConnectionBanner
			unconnectedApps={unconnectedApps}
			onDismiss={hideBanner}
			onConnect={markAppAsConnected}
			forceMultiline={forceMultiline}
		/>
	);
};

const applyInitialEditorDocument = ({
	actions,
	initialPromptText,
	shouldSeedInitialPromptText,
	draftMessage,
}: {
	actions: EditorActions;
	initialPromptText?: string;
	shouldSeedInitialPromptText?: boolean;
	draftMessage?: DocNode | null;
}) => {
	if (
		initialPromptText &&
		(FeatureGates.getExperimentValue('cc_ai_growth_welcome_from_rovo', 'isEnabled', false) ||
			shouldSeedInitialPromptText)
	) {
		// Pre-fill the editor by encoding the prompt as ADF. Any URLs embedded in
		// the prompt are converted into inline smart links.
		actions.replaceDocument(createInitialPromptTextDocument(initialPromptText));
	} else if (initialPromptText && fg('jsm_kb_from_scratch')) {
		const contentNode = {
			type: 'text' as const,
			text: initialPromptText,
		} as TextDefinition;
		const initialDoc: DocNode = {
			type: 'doc',
			version: 1,
			content: [
				{
					type: 'paragraph',
					content: [contentNode],
				},
			],
		};
		actions.replaceDocument(initialDoc);
	} else if (draftMessage) {
		actions.replaceDocument(draftMessage);
	}
};

/**
 * Props crossing the `chat-input-inner` memo boundary.
 *
 * Slot-like ReactNode props (`topAccessory`, `dropdownSlot`,
 * `customSlotAboveActionRow`, and `customSlotBeforeSubmitButton`) are compared
 * by reference by `shallowEqualObjects`. Callers that create those slots from
 * inline JSX should memoize them before passing them through this boundary;
 * otherwise a fresh ReactElement reference will intentionally re-render the
 * inner content.
 */
const ChatInputInternal = forwardRef<ChatPromptInputRef, ChatInputInternalProps>(
	(
		{
			entryPoint,
			mode,
			productKey,
			siteId,
			orgId,
			assistanceServiceParams,
			fileUploadAssistanceOverrides,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			shouldBeCentered: _shouldBeCentered,
			placeholderOverride,
			promptDraftAndModeKey,
			clearModeOnSubmit,
			isAgentTyping,
			arrowKeyPluginConfigMessages,
			autocompleteContext,
			isDisabled = false,
			autoSuggestConfig = undefined,
			useCurrentPageContext = false,
			editorPopupsMountPoint,
			editorPopupsBoundariesElement,
			editorPopupsScrollableElement,
			shouldRenderQuickActionsInPortal,
			shouldRenderChatModeMenusInPortal = false,
			placeholderPrompts,
			hoveredPrompt,
			forceTruncatedWrapperOnEmptyEditor,
			showSingleLineEditor = false,
			hideLinkDisplayText,
			isConversationAgentic,
			conversationAgentId,
			chatSessionId,
			chatTurnNumber,
			selectedAgentForInputHat,
			onSelectedAgentIdChange,
			onSelectedAgentChange,
			onSubmit,
			onAbort,
			onPromptChange,
			disabledAgentTooltip,
			enableAgentMentions = false,
			onFilesChange,
			onKeystroke,
			actions,
			contextProviders,
			onContextBrowserShow,
			onContextAvailable,
			minHeight,
			maxHeight,
			menuPosition,
			contentContext = 'global',
			enableSkills = true,
			enableSkillsInStagingArea = false,
			enableSkillsInSpecialistPromptInput = false,
			enableVoiceDictation = false,
			dictationOverlayBackground,
			enableBrowserContextBanner = false,
			isSpaceScopedConversation,
			spaceSourceCount,
			spaceSources,
			zeroSourcesAction,
			browserContextSanitizer,
			aiFeatureContextOverride,
			showContextBannerForEntityContextOverride = false,
			disableWrapperMarginStyles = false,
			dropdownSlot,
			topAccessory,
			customSlotBeforeChatModeMenu,
			customSlotBeforeSubmitButton,
			customSlotAboveActionRow,
			recentSearchListSize,
			shouldRenderNoResultsImage,
			adaptiveHeight,
			enableMaxMode = true,
			showChatModeMenu = true,
			enableAppsDiscoveryUi,
			urlValidationConfig,
			showActionsButton = true,
			onDiscoverSkillsClick,
			onBrowsePromptLibraryClick,
			onCreateSpaceClick,
			onAutoSuggestQueryChange,
			onTypeaheadStateChange,
			actionsMenuItemsVisibility,
			hasQueuedMessages = false,
			initialPromptText,
			shouldSeedInitialPromptText,
			isBackgroundTransparent = true,
			shouldFocusOnMount = true,
			submitButton: SubmitButton,
			showConnectorTip = true,
			onFilteredAppsChange,
			onWebSearchToggle,
			onOrgKnowledgeToggle,
			onReasoningModeChange,
			blankObjectStyles = false,
			shouldUseEditorPlaceholder = true,
		},
		ref,
	) => {
		di(
			ComposableEditor,
			useEditorPlaceholder,
			useEditorPreset,
			useChatModeStore,
			useChatModeStoreActions,
			useProfileController,
			useRovoUserSettings,
			useWebSearchConfiguration,
			useConversationFiles,
			AtlassianRelayEnvironmentProvider,
			usePromptRouting,
		);

		const assistanceServiceParamsForFileUpload = useMemo(
			() => ({
				...assistanceServiceParams,
				...fileUploadAssistanceOverrides,
			}),
			[assistanceServiceParams, fileUploadAssistanceOverrides],
		);

		const { webSearchEnabled: webSearchConfigEnabled } = useWebSearchConfiguration({
			assistanceServiceParams,
		});
		const isWebSearchPreferenceEnabled = fg('rovo_chat_web_search_preference');
		const { settings: rovoUserSettings } = useRovoUserSettings({
			assistanceServiceParams,
			enabled: isWebSearchPreferenceEnabled,
		});
		const webSearchDefaultEnabled =
			typeof rovoUserSettings?.webSearchDefaultEnabled === 'boolean'
				? rovoUserSettings.webSearchDefaultEnabled
				: undefined;
		const saveRef = useRef<() => void>();
		const shouldUseSeededDefaultValueRef = useRef(true);
		const hasSentWelcomePromptEditedAnalyticsRef = useRef(false);
		const disableSeededDefaultValue = useCallback(() => {
			shouldUseSeededDefaultValueRef.current = false;
		}, []);
		const [inputValueLength, setInputValueLength] = useState(0);
		// Mirrors the latest length into a ref so the imperative `getIsEmpty`
		// (memoised on `editorApi`) reads a current value, not a stale closure.
		const inputValueLengthRef = useRef(inputValueLength);
		inputValueLengthRef.current = inputValueLength;
		const [autoSuggestQuery, setAutoSuggestQuery] = useState(''); // TODO: remove during link processing feture gate cleanup
		const [autoSuggestADF, setAutoSuggestADF] = useState<DocNode | null>(null);
		const [livePromptText, setLivePromptText] = useState(initialPromptText ?? '');
		const customSlotBeforeChatModeMenuRenderer =
			typeof customSlotBeforeChatModeMenu === 'function' ? customSlotBeforeChatModeMenu : undefined;
		const isSelectingSuggestionRef = useRef(false);
		const [hasCodeBlock, setHasCodeBlock] = useState(false);
		const [promptHasSkill, setPromptHasSkill] = useState(false);
		const [taskModeAnimationTrigger, setTaskModeAnimationTrigger] = useState(0);
		const { unconnectedSourceTip, dismissTip, detectUnconnectedSource } = useUnconnectedSourceTip();

		const [autoSuggestTracking, { resetAutoSuggestTracking }] = useAutoSuggestTracking();
		const { sendAnalyticsEvent } = useAnalytics();
		const shouldApplySeededInitialPromptText = useMemo(
			() =>
				!!initialPromptText &&
				!!shouldSeedInitialPromptText &&
				FeatureGates.getExperimentValue('cc_ai_growth_welcome_from_rovo', 'isEnabled', false),
			[initialPromptText, shouldSeedInitialPromptText],
		);
		const sendWelcomeFromRovoChatPromptAnalytics = useCallback(
			(action: 'submitted' | 'edited' | 'cleared', attributes?: Record<string, unknown>) => {
				if (!shouldApplySeededInitialPromptText) {
					return;
				}

				sendAnalyticsEvent({
					eventType: 'track',
					action,
					actionSubject: 'prompt',
					actionSubjectId: 'welcomeFromRovoChat',
					source: 'welcomeFromRovoChat',
					...(attributes ? { attributes } : {}),
				});
			},
			[sendAnalyticsEvent, shouldApplySeededInitialPromptText],
		);
		useEffect(() => {
			hasSentWelcomePromptEditedAnalyticsRef.current = false;
		}, [initialPromptText, promptDraftAndModeKey]);
		const { draftMessage, setDraftMessage } = useDraftsController(promptDraftAndModeKey);
		const { value: mentionResourceOverride } = useRovoMentionProvider();
		const isMentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');
		const experimentEligible = enableAgentMentions;
		const agentMentionsEnabled = experimentEligible && isMentionAgentsExperimentEnabled;

		// Agent typeahead wiring (only consumed when agent mentions are enabled,
		// but hooks must always run). The agent source is a stable, memoised
		// reference while siteId / productKey are unchanged, so it can be passed
		// straight into `useChatMentionResource` without churning the underlying
		// `RovoChatMentionResource`.
		// All prompt-input routing derivation (Rovo-mention recognition, the
		// specialist/Rovo prompt-input split, and the switch-back capability) lives
		// in `usePromptRouting`, which reads the per-conversation selected-agent
		// draft state and combines it with the host-owned inputs
		// (`isConversationAgentic` per conversation; `agentMentionsEnabled` is the
		// integration opt-in combined with the experiment). Stored selected-agent
		// state remains available to the controller when the experiment is off, but
		// this capability keeps it inert for rendering and routing.
		const {
			selectedAgentIds,
			activeSelectedAgentId,
			activeSelectedAgentMetadata,
			setSelectedAgentIds,
			addSelectedAgentId,
			removeSelectedAgentId,
			hasActiveSpecialistAgentMention: hasActiveSpecialistAgentMentionFromDraft,
			isSpecialistPromptInput: isSpecialistPromptInputFromDraft,
			canSwitchBackToRovo,
		} = usePromptRouting(promptDraftAndModeKey, {
			isConversationAgentic,
			enableAgentMentions: agentMentionsEnabled,
		});
		const deactivatedAgentIds = useDeactivatedSelectedAgentIds(promptDraftAndModeKey);
		// `assistanceServiceParams.product` is the canonical product
		// identifier AGG expects on `agentStudio_getAgents` — the rest of
		// the agent-fetching code passes this exact value. The component
		// prop `productKey` is a coarser host-side string that does NOT
		// match the AGG product enumeration and would cause the query to
		// return zero agents.
		const { agentSource, hasAvailableAgents } = useMentionAgentSource({
			enabled: agentMentionsEnabled,
			cloudId: siteId,
			product: assistanceServiceParams.product,
		});
		const isActiveSelectedAgentDefaultRovo =
			agentMentionsEnabled &&
			activeSelectedAgentId !== undefined &&
			isDefaultRovoChat({ agentId: activeSelectedAgentId });
		const isActiveSelectedAgentIdentityAccount = activeSelectedAgentId?.includes(':') ?? false;
		const activeSelectedIdentityAccountId = agentMentionsEnabled
			? isActiveSelectedAgentDefaultRovo
				? undefined
				: (activeSelectedAgentMetadata?.identityAccountId ??
					(!isActiveSelectedAgentIdentityAccount ? undefined : activeSelectedAgentId))
			: activeSelectedAgentMetadata?.identityAccountId;
		const metadataRoutingAgentId = activeSelectedAgentMetadata?.id?.trim();
		const hasKnownMetadataRoutingAgentId =
			metadataRoutingAgentId !== undefined &&
			metadataRoutingAgentId !== activeSelectedAgentMetadata?.identityAccountId;
		const directSelectedRoutingAgentId =
			(hasKnownMetadataRoutingAgentId ? metadataRoutingAgentId : undefined) ||
			(isActiveSelectedAgentDefaultRovo ||
			(activeSelectedAgentMetadata?.identityAccountId === undefined &&
				!isActiveSelectedAgentIdentityAccount)
				? activeSelectedAgentId
				: undefined);
		const shouldLookupActiveSelectedAgent =
			agentMentionsEnabled &&
			activeSelectedIdentityAccountId !== undefined &&
			directSelectedRoutingAgentId === undefined;
		const {
			data: hydratedSelectedRoutingAgentIdData,
			error: selectedAgentRoutingError,
			isFetching: isSelectedAgentRoutingFetching,
		} = useAgentRoutingIdByIdentityAccountId({
			identityAccountId: activeSelectedIdentityAccountId ?? '',
			assistanceServiceParams,
			enabled: shouldLookupActiveSelectedAgent,
		});
		const hydratedSelectedRoutingAgentId = hydratedSelectedRoutingAgentIdData?.trim() || undefined;
		const activeSelectedRoutingAgentId = agentMentionsEnabled
			? (directSelectedRoutingAgentId ?? hydratedSelectedRoutingAgentId ?? undefined)
			: activeSelectedAgentId;
		const isActiveSelectedAgentRoutingUnresolved =
			shouldLookupActiveSelectedAgent && activeSelectedRoutingAgentId === undefined;
		const isActiveSelectedAgentRoutingPending =
			isActiveSelectedAgentRoutingUnresolved &&
			(isSelectedAgentRoutingFetching || selectedAgentRoutingError === null);
		const isResolvedRovoMentioned =
			agentMentionsEnabled && isDefaultRovoChat({ agentId: activeSelectedRoutingAgentId });
		const hasActiveSpecialistAgentMention = agentMentionsEnabled
			? activeSelectedRoutingAgentId !== undefined && !isResolvedRovoMentioned
			: hasActiveSpecialistAgentMentionFromDraft;
		const isSpecialistPromptInput = agentMentionsEnabled
			? isConversationAgentic && !isResolvedRovoMentioned
			: isSpecialistPromptInputFromDraft;
		// `selectedAgentIds` is funnelled through a ref so the mention
		// provider's disabled-state predicate reads the freshest value on
		// every call without us having to rebuild the provider when the
		// active agent changes.
		const selectedAgentIdsRef = useRef<readonly string[]>(selectedAgentIds);
		selectedAgentIdsRef.current = selectedAgentIds;
		const deactivatedAgentIdsRef = useRef<readonly string[]>(deactivatedAgentIds);
		deactivatedAgentIdsRef.current = deactivatedAgentIds;
		const activeSelectedRoutingAgentIdRef = useRef(activeSelectedRoutingAgentId);
		activeSelectedRoutingAgentIdRef.current = activeSelectedRoutingAgentId;
		const activeSelectedAgentMetadataRef = useRef(activeSelectedAgentMetadata);
		activeSelectedAgentMetadataRef.current = activeSelectedAgentMetadata;
		const onSelectedAgentIdChangeRef = useRef(onSelectedAgentIdChange);
		onSelectedAgentIdChangeRef.current = onSelectedAgentIdChange;
		const onSelectedAgentChangeRef = useRef(onSelectedAgentChange);
		onSelectedAgentChangeRef.current = onSelectedAgentChange;
		// Single transition state machine shared with editor-mention reconciliation,
		// so submit/menu transitions and draft hydration decisions stay consistent.
		const selectedAgentTransitionHandle = useSelectedAgentTransition(promptDraftAndModeKey);
		const handleReasoningModeMenuOpenChange =
			selectedAgentTransitionHandle.onReasoningMenuOpenChange;
		const { placeholder, clearPlaceholder } = useEditorPlaceholder();
		const { highlightActionsEditorPlaceholder, clearHighlightActionsEditorPlaceholder } =
			useHighlightActionsEditorPlaceholder();
		const { formatMessage } = useIntl();
		const { showFlag } = useFlags();
		const { files, clearAttachments, anyFilesUploading, handleFilesSelected } =
			useConversationFiles({
				assistanceServiceParams: assistanceServiceParamsForFileUpload,
				key: promptDraftAndModeKey,
			});
		const { externalContextsServiceRepresentations } =
			useConversationExternalAttachments(promptDraftAndModeKey);
		const isRenderStabilizationEnabled = useRovoTtaiSegmentRenderStabilizationEnabled();
		const boundaryActionsMenuItemsVisibility = useBoundaryActionsMenuItemsVisibility(
			actionsMenuItemsVisibility,
			isRenderStabilizationEnabled,
		);
		const boundaryAssistanceServiceParams = useBoundaryAssistanceServiceParams(
			assistanceServiceParams,
			isRenderStabilizationEnabled,
		);

		// Updates consumers with file changes if they pass in a handler function
		useEffect(() => {
			if (onFilesChange) {
				onFilesChange(files.filter((file) => !file.error));
			}
		}, [files, onFilesChange]);
		const {
			getChatMode,
			setReasoningMode,
			setDeepResearchEnabled,
			setWebSearchEnabled,
			seedWebSearchDefault,
			setChatMode,
			clearMode,
			setRovoControlsSuppressed,
		} = useChatModeStoreActions();
		const { hideBanner: hideAppConnectionBanner } = useAppConnectionBannerActions();
		const { propertiesRef: analyticsPropertiesRef, setAnalyticsPropertiesWithoutRerender } =
			useAnalyticsPropertiesContext();
		const chatModeState = useChatModeStore(promptDraftAndModeKey)[0];
		const shouldShowDeepResearchInAddMenu =
			showChatModeMenu && fg('rovo_chat_enable_tasks') && enableMaxMode;
		const isReasoningModeDisabledByDeepResearch =
			shouldShowDeepResearchInAddMenu &&
			chatModeState.deepResearchEnabled &&
			fg('rovo_chat_enable_tasks');
		const handleDeepResearchClick = useCallback(() => {
			setDeepResearchEnabled(promptDraftAndModeKey, !chatModeState.deepResearchEnabled);
		}, [chatModeState.deepResearchEnabled, promptDraftAndModeKey, setDeepResearchEnabled]);
		useViewMoreModalParamsEffect(promptDraftAndModeKey, assistanceServiceParams);
		const appFiltersSetByPromptRef = useRef(chatModeState.appFiltersSetByPrompt);
		appFiltersSetByPromptRef.current = chatModeState.appFiltersSetByPrompt;
		const { aaid: currentUserId } = useProfileController();
		const isInSpaceContext = Boolean(isSpaceScopedConversation);

		const activePageContext = useActiveContext({
			contentContext,
			currentPageAsContextStoreKey: promptDraftAndModeKey,
			enableBrowserContextBanner,
			browserContextSanitizer,
			aiFeatureContextOverride,
		});
		const pageContextType = usePageContextType(UseCase.AUTOCOMPLETE);

		const isDragAndDropEnabled = fg('phx_drag_and_drop_trello_cards_to_rovo_chat');

		const { autoApplyAvailability } = useAutoApplyAvailability(
			contentContext!,
			useCurrentPageContext!,
			isSpecialistPromptInput,
			productKey,
		);
		const isAutoApplyVisible =
			fg('enable_auto_apply_toggling') && autoApplyAvailability.type === 'available';

		const renderAutoApplyToggle = useCallback(() => {
			if (!isAutoApplyVisible) {
				return undefined;
			}
			return (
				<AutoApplyToggleMenuItem
					sessionData={
						currentUserId && siteId ? { userId: currentUserId, cloudId: siteId } : undefined
					}
					contentContext={contentContext}
					useCurrentPageContext={useCurrentPageContext}
					isConversationAgentic={isSpecialistPromptInput}
					productKey={productKey}
					// Custom icon renderer with gradient animation
					renderIcon={(iconProps) => (
						<IconContainer selected={iconProps.selected} ref={iconProps.ref}>
							<EditIcon spacing="spacious" label="" />
						</IconContainer>
					)}
					// Custom toggle renderer to match chat-mode-menu styling
					renderToggleItem={(props) => (
						<ChatModeToggleMenuItem
							toggleId={props.toggleId}
							icon={props.icon}
							title={typeof props.title === 'string' ? props.title : String(props.title)}
							subtitle={props.subtitle ? String(props.subtitle) : undefined}
							onToggle={props.onToggle}
							isActive={props.isActive}
							isDisabled={props.isDisabled}
						/>
					)}
					// Error handling - show flag when toggle fails
					onError={() => {
						showFlag({
							isAutoDismiss: true,
							id: 'auto-apply-toggle-error',
							title: formatMessage(autoApplyToggleMessages.errorFlagTitle),
							description: formatMessage(autoApplyToggleMessages.errorFlagDescription),
							icon: <ErrorIcon spacing="spacious" label="" color={token('color.icon.danger')} />,
						});
					}}
					title={formatMessage(chatModeMessages.autoApplyToggleTitle)}
				/>
			);
		}, [
			currentUserId,
			siteId,
			contentContext,
			productKey,
			useCurrentPageContext,
			isSpecialistPromptInput,
			isAutoApplyVisible,
			showFlag,
			formatMessage,
		]);

		const handleMentionAnalyticsEvent = useCallback(
			(event: MentionAnalyticsEvent) => {
				sendMentionMenuAnalyticsEvent({
					event,
					previousAgentId: activeSelectedRoutingAgentIdRef.current,
					previousAgentName: activeSelectedAgentMetadataRef.current?.name,
					previousAgentCreatorType: activeSelectedAgentMetadataRef.current?.creatorType,
					conversationAgentId,
					chatSessionId,
					chatTurnNumber,
					sendAnalyticsEvent,
				});
			},
			[sendAnalyticsEvent, conversationAgentId, chatSessionId, chatTurnNumber],
		);

		// Instrumentation is independent from agent visibility. The analytics gate
		// collects the all-user people/menu baseline, while agent-only events are
		// emitted only for the agent-exposed experience.
		const shouldCollectMentionAnalytics = fg('rovo_chat_mention_agents_analytics');
		const shouldCollectAgentAnalytics = shouldCollectMentionAnalytics && agentMentionsEnabled;
		const shouldCollectAgentAnalyticsRef = useRef(shouldCollectAgentAnalytics);
		shouldCollectAgentAnalyticsRef.current = shouldCollectAgentAnalytics;

		const { reconcileAgentMentions, handleEditorMentionsChanged } =
			useReconcileSelectedAgentsWithEditorMentions({
				enableAgentMentions: agentMentionsEnabled,
				promptDraftAndModeKey,
				draftMessage,
				selectedAgentTransitionHandle,
				selectedAgentIdsRef,
				deactivatedAgentIdsRef,
				addSelectedAgentId,
				removeSelectedAgentId,
			});

		const getEnhancedAgentMentionNodeId = useCallback(
			(agent: MentionAgent) => normalizeAccountIdFromIdentityAri(agent.identityAccountId) ?? null,
			[],
		);

		const mentionResource = useChatMentionResource({
			productKey,
			siteId,
			orgId,
			currentUserId,
			mentionResourceOverride,
			enableAgents: agentMentionsEnabled,
			agentSource,
			onMentionAnalyticsEvent: shouldCollectMentionAnalytics
				? handleMentionAnalyticsEvent
				: undefined,
			onAgentSelected: (agent) => {
				onSelectedAgentChangeRef.current?.({
					id: agent.id,
					identityAccountId: agent.identityAccountId,
				});
				const selectedAgentId = agentMentionsEnabled
					? (normalizeAccountIdFromIdentityAri(agent.identityAccountId) ?? agent.id)
					: agent.id;
				addSelectedAgentId(selectedAgentId, {
					id: agent.id,
					name: agent.name,
					identityAccountId: agent.identityAccountId,
					creatorType: agent.creatorType,
				});
			},
			// Only feed the disabled-state predicate when agent mentions are
			// enabled; otherwise we leave the predicate undefined so chips
			// are never disabled.
			getSelectedAgentIds: agentMentionsEnabled ? () => selectedAgentIdsRef.current : undefined,
			getAgentMentionNodeId: agentMentionsEnabled ? getEnhancedAgentMentionNodeId : undefined,
			// Dedicated (non-default) agent chats show the "Rovo" mention row even before an
			// agent is mentioned in the input.
			isDedicatedAgentChat: agentMentionsEnabled && isConversationAgentic,
			rovoMentionLabel: agentMentionsEnabled ? formatMessage(messages.rovoMentionName) : undefined,
			disabledAgentTooltip,
		});
		// When the active agent selection changes, ping the mention provider
		// so already-rendered chips re-evaluate their disabled state. This is
		// necessary because the ProseMirror `MentionNodeView` only re-runs
		// `updateState` when the editor's shared mention state changes — it
		// otherwise has no way to know that the consumer's predicate inputs
		// (which it reads via `MentionProvider.getMentionDisabledState`) have
		// changed.
		useEffect(() => {
			if (!agentMentionsEnabled) {
				return;
			}
			let cancelled = false;
			void mentionResource.then((provider) => {
				if (cancelled) {
					return;
				}
				// Guard against providers that don't implement the optional
				// notification method (e.g. tests using a bare mock provider).
				const maybeNotifier = provider as {
					notifyDisabledStateChanged?: () => void;
				};
				maybeNotifier.notifyDisabledStateChanged?.();
			});
			return () => {
				cancelled = true;
			};
			// `mentionResource` is intentionally a stable Promise across renders
			// while the deps of `useChatMentionResource` are unchanged. The
			// effect re-runs whenever the active selection changes.
		}, [selectedAgentIds, mentionResource, agentMentionsEnabled]);

		const selectedAgentRestoreVisibilityKey = agentMentionsEnabled
			? selectedAgentForInputHat?.id
			: undefined;
		useEffect(() => {
			const selectedAgentChange =
				agentMentionsEnabled && activeSelectedRoutingAgentId
					? {
							id: activeSelectedRoutingAgentId,
							identityAccountId: activeSelectedIdentityAccountId,
						}
					: undefined;

			onSelectedAgentChangeRef.current?.(selectedAgentChange);
			onSelectedAgentIdChangeRef.current?.(selectedAgentChange?.id);
		}, [
			activeSelectedRoutingAgentId,
			activeSelectedIdentityAccountId,
			agentMentionsEnabled,
			promptDraftAndModeKey,
			selectedAgentRestoreVisibilityKey,
		]);

		const selectedAgentInputHatImageUrl = selectedAgentForInputHat?.iconUrl;

		const handleSelectedAgentDismiss = useCallback(() => {
			if (activeSelectedAgentId) {
				const activeSelectedRoutingAgentId = activeSelectedRoutingAgentIdRef.current;
				if (shouldCollectAgentAnalyticsRef.current && activeSelectedRoutingAgentId) {
					sendAgentMentionDismissedAnalyticsEvent({
						agentId: activeSelectedRoutingAgentId,
						selectedAgentCountBeforeDismiss: selectedAgentIdsRef.current.length,
						conversationAgentId,
						sendAnalyticsEvent,
					});
				}
				removeSelectedAgentId(activeSelectedAgentId);
			}
		}, [activeSelectedAgentId, removeSelectedAgentId, sendAnalyticsEvent, conversationAgentId]);

		useEffect(() => {
			return () => {
				onSelectedAgentChangeRef.current?.(undefined);
				onSelectedAgentIdChangeRef.current?.(undefined);
			};
		}, []);

		// Keep the visible Rovo-owned controls in sync with who owns the next turn:
		// the store's `setRovoControlsSuppressed` action owns the snapshot/reset/restore
		// (see its return contract); this effect just maps inputs to that action and
		// forwards the applied values to the `on*Change` props. Gated on
		// `agentMentionsEnabled` so it is inert unless agent mentions are on.
		const onReasoningModeChangeRef = useRef(onReasoningModeChange);
		onReasoningModeChangeRef.current = onReasoningModeChange;
		const onWebSearchToggleRef = useRef(onWebSearchToggle);
		onWebSearchToggleRef.current = onWebSearchToggle;
		const onFilteredAppsChangeRef = useRef(onFilteredAppsChange);
		onFilteredAppsChangeRef.current = onFilteredAppsChange;
		useEffect(() => {
			if (!isWebSearchPreferenceEnabled || webSearchDefaultEnabled === undefined) {
				return;
			}
			if (seedWebSearchDefault(promptDraftAndModeKey, webSearchDefaultEnabled)) {
				onWebSearchToggleRef.current?.(webSearchDefaultEnabled);
			}
		}, [
			isWebSearchPreferenceEnabled,
			promptDraftAndModeKey,
			seedWebSearchDefault,
			webSearchDefaultEnabled,
		]);
		const rovoControlsSuppressed = areRovoControlsSuppressed({
			isAgentMentionActive: hasActiveSpecialistAgentMention,
			isSpecialistPromptInput,
		});
		useEffect(() => {
			if (!agentMentionsEnabled) {
				return;
			}
			const applied = setRovoControlsSuppressed(promptDraftAndModeKey, {
				suppressed: rovoControlsSuppressed,
			});
			if (applied) {
				onReasoningModeChangeRef.current?.(applied.reasoningMode);
				onWebSearchToggleRef.current?.(applied.webSearchEnabled);
				onFilteredAppsChangeRef.current?.(applied.appFilters ?? []);
			}
		}, [
			agentMentionsEnabled,
			promptDraftAndModeKey,
			setRovoControlsSuppressed,
			rovoControlsSuppressed,
		]);

		const inputExceedsMaxLimit = inputValueLength > MAX_CHARACTERS_INPUT;
		const hasSubmittableText = inputValueLength > 0;
		const hasSubmittableAttachment = files.some((file) => !file.error && !file.isLoading);
		const isSendDisabled =
			isDisabled ||
			(!hasSubmittableText && !hasSubmittableAttachment) ||
			inputExceedsMaxLimit ||
			(isAgentTyping && !fg('rovo_chat_message_queue')) ||
			anyFilesUploading ||
			isActiveSelectedAgentRoutingUnresolved;

		const charactersOverLimit = inputExceedsMaxLimit ? inputValueLength - MAX_CHARACTERS_INPUT : 0;

		let submitButtonTooltip: string | undefined;
		if (isSendDisabled && inputExceedsMaxLimit) {
			submitButtonTooltip = `${charactersOverLimit} character${
				charactersOverLimit === 1 ? '' : 's'
			} over. Try shortening your message.`;
		} else if (isActiveSelectedAgentRoutingPending) {
			submitButtonTooltip = formatMessage(messages.agentRoutingResolving);
		} else if (isActiveSelectedAgentRoutingUnresolved) {
			submitButtonTooltip = formatMessage(messages.agentRoutingFailed);
		}

		const handleSubmit = useCallback(async () => {
			if (isSendDisabled) {
				return;
			}

			const value: DocNode = normalizeConnectorSourceDoc(await actions.getValue());
			sendWelcomeFromRovoChatPromptAnalytics('submitted', {
				wasInitialPromptModified: !shouldUseSeededDefaultValueRef.current,
			});

			const {
				deepResearchEnabled,
				thinkDeeperEnabled,
				alphaExtendedThinkingMode: derivedAlphaExtendedThinkingMode,
				fastModeEnabled,
				taskModeEnabled,
				sourceMode,
				webSearchEnabled,
				appFilters,
				useCurrentPageContext,
			} = getChatMode(promptDraftAndModeKey);
			// `skillSelectionSource` is recorded by the surface that inserts a skill placeholder
			// (editor typeahead, inline browse, auto-suggest, prompt library) into the
			// AnalyticsPropertiesContext, then read here once at submit and cleared so the next
			// non-skill submission does not inherit a stale value.
			const skillSelectionSource: SkillSelectionSource | undefined =
				analyticsPropertiesRef.current.skillSelectionSource;
			const alphaExtendedThinkingMode = !isSpecialistPromptInput
				? derivedAlphaExtendedThinkingMode
				: undefined;
			const validFiles = files.filter((file) => !file.error);

			// Fire autosuggest usage analytics if there was any interaction
			if (
				autoSuggestConfig &&
				(autoSuggestTracking.suggestionsCyclesShownCount > 0 ||
					autoSuggestTracking.suggestionSelectionCount > 0)
			) {
				// Calculate edit distances for all selected suggestions
				const { processedText } = extractContent(value);
				const editDistancePerSelection = calculateEditDistances(
					autoSuggestTracking.selectedTexts,
					processedText,
				);

				// Calculate averages from the metrics
				const avgEditDistance =
					editDistancePerSelection.length > 0
						? editDistancePerSelection.reduce((sum: number, m) => sum + m.editDistance, 0) /
							editDistancePerSelection.length
						: 0;

				const avgMaxNormalizedEditRate =
					editDistancePerSelection.length > 0
						? editDistancePerSelection.reduce(
								(sum: number, m) => sum + m.maxNormalizedEditRate,
								0,
							) / editDistancePerSelection.length
						: 0;

				sendAnalyticsEvent({
					eventType: 'track',
					action: 'submitted',
					actionSubject: 'prompt',
					actionSubjectId: 'autoSuggestUsage',
					attributes: {
						promptLength: inputValueLength,
						suggestionCyclesShownCount: autoSuggestTracking.suggestionsCyclesShownCount,
						suggestionSelectionCount: autoSuggestTracking.suggestionSelectionCount,
						totalSuggestionsShownCount: autoSuggestTracking.totalSuggestionsShownCount,
						suggestionCycles: autoSuggestTracking.suggestionCycles,
						avgEditDistance,
						avgMaxNormalizedEditRate,
						editDistancePerSelection,
						source: autoSuggestConfig.source,
					},
				});
			}

			if (shouldCollectAgentAnalyticsRef.current && activeSelectedRoutingAgentId) {
				const selectedAgentCount = selectedAgentIdsRef.current.length;
				sendAgentMentionSubmittedAnalyticsEvent({
					agentId: activeSelectedRoutingAgentId,
					selectedAgentCount,
					conversationAgentId,
					chatSessionId,
					chatTurnNumber,
					sendAnalyticsEvent,
				});
			}

			// Keep these Rovo-owned control gates in lock-step with the reset/restore
			// effect via `areRovoControlsSuppressed` (single source of truth): reasoning
			// modes gate on `!isSpecialistPromptInput`; web search / app filters gate on
			// the full union through the shared helper.
			onSubmit(
				value,
				{
					deepResearchEnabled: !isSpecialistPromptInput && deepResearchEnabled,
					thinkDeeperEnabled: !isSpecialistPromptInput && thinkDeeperEnabled,
					...(alphaExtendedThinkingMode ? { alphaExtendedThinkingMode } : {}),
					fastModeEnabled: !isSpecialistPromptInput && fastModeEnabled,
					taskModeEnabled: !isSpecialistPromptInput && enableMaxMode && taskModeEnabled,
					webSearchEnabled: getWebSearchEnabledForSubmit({
						isAgentMentionActive: hasActiveSpecialistAgentMention,
						isSpecialistPromptInput,
						webSearchConfigEnabled,
						webSearchEnabled,
					}),
					appFilters: getAppFiltersForSubmit({
						isAgentMentionActive: hasActiveSpecialistAgentMention,
						isSpecialistPromptInput,
						appFilters,
					}),
					sourceMode: getSourceModeForSubmit({
						isAgentMentionActive: hasActiveSpecialistAgentMention,
						isSpecialistPromptInput,
						sourceMode:
							isInSpaceContext && fg('rovo_chat_space_source_mode') ? sourceMode : undefined,
					}),
					addCurrentPageAsContext: useCurrentPageContext,
					// The context banner is visible when the space context banner is shown OR when
					// useCurrentPageContext is on and there is an active page context.
					shouldExtractConversationScopes:
						isInSpaceContext || (useCurrentPageContext && !!activePageContext),
					// Only meaningful for explicit skill invocations; processStream gates this
					// further when stamping it onto skillsInvoked entries.
					skillSelectionSource,
					...(agentMentionsEnabled && activeSelectedRoutingAgentId
						? {
								inputSelectedAgentId: activeSelectedRoutingAgentId,
								messageAgentIdentity: {
									agentId: activeSelectedRoutingAgentId,
									identityAccountId: activeSelectedIdentityAccountId,
								},
							}
						: {}),
				},
				validFiles,
				externalContextsServiceRepresentations,
			);

			// Always clear the analytics-context selection source after a submit so the next
			// non-skill prompt does not inherit a stale value. Safe to call unconditionally:
			// when the field is already undefined this is a no-op write.
			if (skillSelectionSource !== undefined) {
				setAnalyticsPropertiesWithoutRerender({ skillSelectionSource: undefined });
			}

			if (clearModeOnSubmit) {
				clearMode(
					promptDraftAndModeKey,
					isWebSearchPreferenceEnabled
						? { webSearchDefaultEnabled, preserveWebSearchEnabled: true }
						: undefined,
				);
			}

			if (agentMentionsEnabled) {
				setSelectedAgentIds(activeSelectedAgentId ? [activeSelectedAgentId] : []);
			}

			// Reset autosuggest tracking for next input session
			resetAutoSuggestTracking();

			// Clean up. `actions.clear()` destroys mention node views; ignore those
			// destroy notifications because this is a submit reset, not a user deleting
			// the selected-agent chip from the draft.
			if (agentMentionsEnabled) {
				selectedAgentTransitionHandle.onSubmitClearChange(true);
			}
			actions.clear();
			if (agentMentionsEnabled) {
				queueMicrotask(() => {
					selectedAgentTransitionHandle.onSubmitClearChange(false);
				});
			}
			setDraftMessage(null);
			clearAttachments(promptDraftAndModeKey);

			// Synchronously reset autosuggest state here rather than relying on the
			// editor's deferred onChange callback. When sending the first message in
			// an empty conversation the chat tree re-mounts before the deferred
			// onChange flushes, so the consumer's autosuggest searchQuery never gets
			// reset to '' and the typeahead overlay stays open. Clearing here
			// mirrors the cleanup performed on editor blur and guarantees the menu
			// closes regardless of which mount the next render lands in.
			setAutoSuggestADF(null);
			setAutoSuggestQuery('');
			if (customSlotBeforeChatModeMenuRenderer) {
				setLivePromptText('');
			}
			onAutoSuggestQueryChange?.('');

			// Hide the app connection banner if it's showing
			hideAppConnectionBanner();
		}, [
			isSpecialistPromptInput,
			webSearchConfigEnabled,
			actions,
			clearAttachments,
			files,
			getChatMode,
			hideAppConnectionBanner,
			isSendDisabled,
			agentMentionsEnabled,
			onSubmit,
			clearMode,
			promptDraftAndModeKey,
			clearModeOnSubmit,
			isWebSearchPreferenceEnabled,
			webSearchDefaultEnabled,
			setDraftMessage,
			externalContextsServiceRepresentations,
			sendAnalyticsEvent,
			inputValueLength,
			autoSuggestTracking,
			resetAutoSuggestTracking,
			autoSuggestConfig,
			analyticsPropertiesRef,
			setAnalyticsPropertiesWithoutRerender,
			activeSelectedAgentId,
			activeSelectedIdentityAccountId,
			activeSelectedRoutingAgentId,
			setSelectedAgentIds,
			activePageContext,
			isInSpaceContext,
			hasActiveSpecialistAgentMention,
			enableMaxMode,
			sendWelcomeFromRovoChatPromptAnalytics,
			onAutoSuggestQueryChange,
			customSlotBeforeChatModeMenuRenderer,
			setAutoSuggestADF,
			setAutoSuggestQuery,
			conversationAgentId,
			chatSessionId,
			chatTurnNumber,
			selectedAgentTransitionHandle,
		]);

		saveRef.current = handleSubmit;

		const [allKnowledgeSources] = useEnabledKnowledgeSources();
		const connectorSources = useMemo(
			() =>
				allKnowledgeSources
					.filter((s) => s.friendlyName)
					.map((s) => ({
						key: s.friendlyName.toLowerCase(),
						display: s.friendlyName,
						source: s.source,
						iconUrl: s.iconUrl,
					}))
					.sort((a, b) => b.key.length - a.key.length),
			[allKnowledgeSources],
		);

		const { editorApi, preset } = useEditorPreset({
			onSave: () => saveRef.current?.(),
			arrowKeyPluginConfig: {
				userMessages: arrowKeyPluginConfigMessages,
			},
			productKey,
			siteId,
			placeholderPrompts,
			linkPickerOptions: {
				hideDisplayText: hideLinkDisplayText,
				recentSearchListSize,
				adaptiveHeight,
				shouldRenderNoResultsImage,
			},
			connectorSources,
			autocomplete: autocompleteContext,
			handleMentionsChanged: agentMentionsEnabled ? handleEditorMentionsChanged : undefined,
		});

		const { insertUrls } = useUrlInsertion(editorApi);

		const assistiveLabel = useMemo(() => {
			if (placeholderOverride !== undefined) {
				return placeholderOverride;
			}

			if (chatModeState.deepResearchEnabled && !fg('rovo_chat_enable_tasks')) {
				return formatMessage(messages.deepResearchAssistiveLabel);
			}

			return formatMessage(messages.defaultAssistiveLabel);
		}, [placeholderOverride, formatMessage, chatModeState.deepResearchEnabled]);

		useEffect(() => {
			if (editorApi?.placeholder && hoveredPrompt) {
				editorApi?.core.actions.execute(
					editorApi?.placeholder?.commands.setPlaceholder(hoveredPrompt),
				);
			} else if (editorApi?.placeholder && placeholderPrompts) {
				editorApi?.core.actions.execute(editorApi?.placeholder?.commands.setPlaceholder(''));
				editorApi?.core.actions.execute(
					editorApi?.placeholder?.commands.setAnimatingPlaceholderPrompts(placeholderPrompts),
				);
			} else if (editorApi?.placeholder && !placeholderPrompts) {
				editorApi?.core.actions.execute(
					editorApi?.placeholder?.commands.setPlaceholder(assistiveLabel),
				);
			}
		}, [
			hoveredPrompt,
			editorApi?.core.actions,
			editorApi?.placeholder,
			placeholderPrompts,
			assistiveLabel,
		]);

		// Expose the focus via ref
		useImperativeHandle(
			ref,
			() => ({
				focus: () => {
					editorApi?.core.actions.focus();
				},
				replaceDocument: (doc: DocNode) => {
					editorApi?.core.actions.replaceDocument(doc);
					editorApi?.core.actions.focus();
				},
				getIsEmpty: () => inputValueLengthRef.current === 0,
			}),
			[editorApi],
		);

		const closeTypeAhead = useRenderStabilizedCallback(
			() => {
				editorApi?.typeAhead?.actions.close({ insertCurrentQueryAsRawText: false });
			},
			[editorApi?.typeAhead?.actions],
			isRenderStabilizationEnabled,
		);

		// When an agent is already mentioned, override the mention type-ahead's "Agents" heading to "Switch to"; otherwise keep the plugin default.
		// Re-assert on mention type-ahead state changes because `updateSectionTitle` is session-scoped and sections rebuild as results load.
		useEffect(() => {
			if (!agentMentionsEnabled || !editorApi?.typeAhead?.sharedState) {
				return;
			}
			const unsubscribe = editorApi.typeAhead.sharedState.onChange(
				({
					nextSharedState,
				}: {
					nextSharedState: { isOpen?: boolean; triggerHandler?: { trigger?: string } } | undefined;
				}) => {
					// Only act when the mention (`@`) type-ahead is open and an
					// agent is already mentioned; otherwise keep the plugin default.
					if (
						!nextSharedState?.isOpen ||
						nextSharedState.triggerHandler?.trigger !== '@' ||
						selectedAgentIdsRef.current.length === 0
					) {
						return;
					}
					editorApi?.mention?.actions?.updateSectionTitle?.({
						id: 'agents',
						title: formatMessage(messages.mentionAgentsSectionSwitch),
					});
				},
			);
			return unsubscribe;
		}, [
			agentMentionsEnabled,
			editorApi?.typeAhead?.sharedState,
			editorApi?.mention?.actions,
			formatMessage,
		]);

		// Notify consumer when the Quick Insert typeahead opens or closes
		useEffect(() => {
			if (!onTypeaheadStateChange || !editorApi?.typeAhead?.sharedState) {
				return;
			}
			const unsubscribe = editorApi.typeAhead.sharedState.onChange(
				({ nextSharedState }: { nextSharedState: { isOpen?: boolean } | undefined }) => {
					onTypeaheadStateChange(nextSharedState?.isOpen ?? false);
				},
			);
			return unsubscribe;
		}, [onTypeaheadStateChange, editorApi?.typeAhead?.sharedState]);

		// Inserts the audio transcription text into the editor
		const handleInsertTranscript = useRenderStabilizedCallback(
			(transcript: string) => {
				closeTypeAhead();
				actions.appendText(`${transcript.trimEnd()} `);
				actions.focus();
			},
			[actions, closeTypeAhead],
			isRenderStabilizationEnabled,
		);

		// Closes the editor toolbar by moving the cursor to the end of the current selection
		// If the selection is in a codeblock, moves to the end of the codeblock instead
		const closeEditorToolbar = useRenderStabilizedCallback(
			() => {
				editorApi?.core?.actions?.execute(({ tr }) => {
					const { selection } = tr;
					const $anchor = selection.$anchor;
					let targetPos = selection.$to.pos;

					let codeBlockNode = $anchor.node($anchor.depth);
					if (codeBlockNode.type.name === 'codeBlock') {
						const codeBlockStart = $anchor.start($anchor.depth);
						targetPos = codeBlockStart + 1 + codeBlockNode.content.size;
					}

					const resolvedPos = tr.doc.resolve(targetPos);

					const newSelection = TextSelection.near(resolvedPos);
					return tr.setSelection(newSelection);
				});
			},
			[editorApi?.core?.actions],
			isRenderStabilizationEnabled,
		);

		const openMentionTypeAhead = useRenderStabilizedCallback(
			() => {
				closeTypeAhead();
				// Small delay to ensure the editor regains focus after closing the dropdown menu
				// This prevents the typeahead from immediately closing in Safari
				// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
				requestAnimationFrame(() => {
					editorApi?.core.actions.focus();
					editorApi?.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
				});
			},
			[closeTypeAhead, editorApi?.core.actions, editorApi?.mention?.actions],
			isRenderStabilizationEnabled,
		);

		const { openLinkTypeAheadFromMenu, openLinkTypeAheadFromPlaceholder } = useLinkTypeAhead({
			editorApi,
			closeTypeAhead,
			isRenderStabilizationEnabled,
		});

		const openAddTypeAhead = useRenderStabilizedCallback(
			() => {
				closeTypeAhead();
				// Small delay to ensure the editor regains focus after closing the dropdown menu
				// This prevents the typeahead from immediately closing in Safari
				// Original root cause research: https://product-fabric.atlassian.net/browse/AFC-550
				requestAnimationFrame(() => {
					editorApi?.core.actions.focus();
					editorApi?.quickInsert?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
				});
			},
			[closeTypeAhead, editorApi?.core.actions, editorApi?.quickInsert?.actions],
			isRenderStabilizationEnabled,
		);

		const tooltipOptions = BUTTON_TOOLTIP_OPTIONS;

		useEffect(() => {
			if (shouldUseEditorPlaceholder && !!placeholder && !!editorApi) {
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'inserted',
					actionSubject: 'editorPlaceholder',
					actionSubjectId: 'rovoChatPromptInput',
					attributes: {
						placeholderType: placeholder.type,
						hasAdf: !!placeholder.adfDocument,
						insertAtCursor: !!placeholder.insertAtCursor,
					},
				});

				// Insert at current cursor position
				if (placeholder.insertAtCursor && placeholder.adfDocument) {
					const paragraphBlock = placeholder.adfDocument.content?.[0];
					const inlineContent =
						paragraphBlock?.type === 'paragraph' && paragraphBlock.content
							? paragraphBlock.content
							: [];
					if (inlineContent.length > 0) {
						actions.replaceSelection(inlineContent);
					}
					actions.focus();
					clearPlaceholder();
					return;
				}

				actions.clear();
				if (placeholder.shouldFocus === false) {
					clearPlaceholder();
					return;
				}

				actions.focus();
				closeTypeAhead();
				// Use ADF document to preserve formatting
				if (placeholder.adfDocument) {
					actions.replaceDocument(placeholder.adfDocument);
				} else {
					actions.appendText(placeholder.prompt);
				}

				editorApi?.core.actions.execute(({ tr }) => {
					const maxPos = tr.doc.content.size;
					const startPos = Math.min(placeholder.selectionStartIndex, maxPos);
					const endPos = Math.min(placeholder.selectionEndIndex, maxPos);

					const transaction = tr.setSelection(
						TextSelection.between(tr.doc.resolve(startPos), tr.doc.resolve(endPos)),
					);

					return transaction;
				});

				// Wait for next browser frame to ensure editor focus and selection are stable
				// This prevents the typeahead from flashing when triggered from surfaces where rovo chat is not already open
				requestAnimationFrame(() => {
					switch (placeholder.type) {
						case 'person':
							openMentionTypeAhead();
							break;
						case 'link':
							openLinkTypeAheadFromPlaceholder();
							break;
						default:
							actions.focus();
							break;
					}
				});

				clearPlaceholder();
			}
		}, [
			editorApi,
			actions,
			clearPlaceholder,
			closeTypeAhead,
			editorApi?.core.actions,
			openLinkTypeAheadFromPlaceholder,
			openMentionTypeAhead,
			placeholder,
			sendAnalyticsEvent,
			shouldUseEditorPlaceholder,
		]);

		useEffect(() => {
			if (!!highlightActionsEditorPlaceholder && !!editorApi) {
				actions.clear();
				actions.focus();
				closeTypeAhead();
				editorApi.core.actions.replaceDocument(highlightActionsEditorPlaceholder);
				editorApi?.core.actions.execute(({ tr }) => {
					const lastNode = tr.doc.lastChild;
					if (lastNode) {
						const lastNodePos = tr.doc.resolve(tr.doc.content.size - lastNode.nodeSize);
						const endPos = tr.doc.resolve(tr.doc.content.size);
						const transaction = tr.setSelection(TextSelection.between(lastNodePos, endPos));

						return transaction;
					}

					return null;
				});
				// This is a workaround to avoid the focus being lost in Jira (and Jira sub-products)
				if (productKey === 'jira' || productKey === 'jsm' || productKey === 'jpd') {
					requestAnimationFrame(() => {
						setTimeout(() => {
							actions.focus();
						}, 1500);
					});
				}
				clearHighlightActionsEditorPlaceholder();
			}
		}, [
			editorApi,
			actions,
			clearHighlightActionsEditorPlaceholder,
			closeTypeAhead,
			highlightActionsEditorPlaceholder,
			productKey,
		]);

		const clearSeededInitialPromptText = useCallback(() => {
			sendWelcomeFromRovoChatPromptAnalytics('cleared');
			disableSeededDefaultValue();
			actions.clear();
			setInputValueLength(0);
			setAutoSuggestADF(null);
			setAutoSuggestQuery('');
			if (customSlotBeforeChatModeMenuRenderer) {
				setLivePromptText('');
			}
			onAutoSuggestQueryChange?.('');
			onPromptChange?.(null);
			setDraftMessage(null);
		}, [
			actions,
			disableSeededDefaultValue,
			onAutoSuggestQueryChange,
			onPromptChange,
			customSlotBeforeChatModeMenuRenderer,
			sendWelcomeFromRovoChatPromptAnalytics,
			setDraftMessage,
		]);

		const isEditorCursorAtEnd = useCallback(() => {
			let isCursorAtEnd = false;
			editorApi?.core.actions.execute(({ tr }) => {
				const { selection } = tr;
				isCursorAtEnd = selection.empty && selection.from >= tr.doc.content.size - 1;

				return null;
			});

			return isCursorAtEnd;
		}, [editorApi?.core.actions]);

		const handleKeyDown: React.KeyboardEventHandler = useCallback(
			(e) => {
				onKeystroke?.(e.nativeEvent);

				if (
					e.key === 'Backspace' &&
					shouldApplySeededInitialPromptText &&
					shouldUseSeededDefaultValueRef.current &&
					FeatureGates.getExperimentValue('cc_ai_growth_welcome_from_rovo', 'isEnabled', false)
				) {
					if (isEditorCursorAtEnd()) {
						e.preventDefault();
						e.stopPropagation();
						clearSeededInitialPromptText();
						return;
					}

					disableSeededDefaultValue();
				}

				// Handle Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) for submission
				if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit();
					return;
				}

				if (e.key === 'Escape' && isAgentTyping) {
					const {
						deepResearchEnabled,
						thinkDeeperEnabled,
						fastModeEnabled,
						taskModeEnabled,
						webSearchEnabled,
					} = getChatMode(promptDraftAndModeKey);
					onAbort(
						{
							deepResearchEnabled,
							thinkDeeperEnabled,
							fastModeEnabled,
							taskModeEnabled: enableMaxMode && taskModeEnabled,
							webSearchEnabled,
						},
						undefined,
					);

					// Somehow the focus is lost after pressing escape, so subsequent key presses are not captured
					e.stopPropagation();
				}
			},
			[
				onKeystroke,
				isEditorCursorAtEnd,
				clearSeededInitialPromptText,
				onAbort,
				isAgentTyping,
				getChatMode,
				promptDraftAndModeKey,
				handleSubmit,
				enableMaxMode,
				shouldApplySeededInitialPromptText,
				disableSeededDefaultValue,
			],
		);

		const handleSuggestionSelect = useRenderStabilizedCallback(
			(suggestion: AutoSuggestOption) => {
				actions.clear();

				isSelectingSuggestionRef.current = true;

				if (suggestion.adf) {
					try {
						// Insert the ADF content directly into the editor
						editorApi?.core.actions.replaceDocument(suggestion.adf);
					} catch {
						// Fallback to text content if ADF insertion fails
						actions.appendText(suggestion.text);
					}
				} else {
					actions.appendText(suggestion.text);
				}
				actions.focus();

				setAutoSuggestADF(null);
				setAutoSuggestQuery('');
				onAutoSuggestQueryChange?.('');
			},
			[actions, editorApi?.core.actions, onAutoSuggestQueryChange],
			isRenderStabilizationEnabled,
		);

		const handleEditorBlur = useCallback(() => {
			setAutoSuggestADF(null);
			setAutoSuggestQuery('');
			onAutoSuggestQueryChange?.('');
		}, [onAutoSuggestQueryChange]);

		const handleUrlsSelected = useRenderStabilizedCallback(
			(urls: string[]) => {
				if (!isDragAndDropEnabled) {
					return;
				}

				insertUrls(urls, 'rovo-chat-editor-dropzone');
			},
			[insertUrls, isDragAndDropEnabled],
			isRenderStabilizationEnabled,
		);

		useSubscribe(
			{ topic: 'ai-mate-chat-inserts', triggerLatest: true },
			useCallback(
				(payload) => {
					if (payload.type === 'insert-skill-into-prompt-input') {
						const paragraphBlock = createSkillAdf(payload.data.skill as Skill).content?.[0];
						const inlineContent =
							paragraphBlock?.type === 'paragraph' && paragraphBlock.content
								? paragraphBlock.content
								: [];
						if (inlineContent.length > 0) {
							actions.replaceSelection(inlineContent);
							actions.focus();
							if (payload.data.skillSelectionSource) {
								setAnalyticsPropertiesWithoutRerender({
									skillSelectionSource: payload.data.skillSelectionSource as SkillSelectionSource,
								});
							}
						}
						return;
					}

					if (!isDragAndDropEnabled || payload.type !== 'insert-urls-into-prompt-input') {
						return;
					}

					const filteredUrls = validUrls(payload.data.urls, urlValidationConfig);
					if (filteredUrls.length > 0) {
						insertUrls(filteredUrls, 'pubsub-insert-url-event');
					}
				},
				[
					actions,
					insertUrls,
					isDragAndDropEnabled,
					setAnalyticsPropertiesWithoutRerender,
					urlValidationConfig,
				],
			),
		);

		const shouldRenderVoiceEnabledFooterRow = useIsSpeechToTextEnabled(enableVoiceDictation);

		const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false);
		const handleMicPermissionDenied = useCallback(() => setIsMicPermissionDenied(true), []);

		const stableEditorPropsLatest = {
			appFiltersSetByPromptRef,
			detectUnconnectedSource,
			draftMessage,
			editorApi,
			hideAppConnectionBanner,
			initialPromptText,
			shouldApplySeededInitialPromptText,
			isSelectingSuggestionRef,
			onAutoSuggestQueryChange,
			onPromptChange,
			promptHasSkill,
			promptDraftAndModeKey,
			customSlotBeforeChatModeMenuRenderer,
			setAutoSuggestADF,
			setAutoSuggestQuery,
			setChatMode,
			setDraftMessage,
			setLivePromptText,
		};
		const stableEditorPropsLatestRef = useRef(stableEditorPropsLatest);
		stableEditorPropsLatestRef.current = stableEditorPropsLatest;

		const deferredOnChangeFrameRef = useRef<number | null>(null);
		const deferredOnChangeVersionRef = useRef(0);
		const deferredOnChangePayloadRef = useRef<{
			textContent: string;
			promptHasSkill: boolean;
		} | null>(null);

		useEffect(() => {
			return () => {
				// On unmount we intentionally drop any pending deferred onChange side effects
				// (autosuggest, draft persistence, onPromptChange) instead of flushing them: the
				// editor subtree unmounts first, so a flush's requestDocument would no-op anyway.
				// Message submission is unaffected because handleSubmit reads the live document via
				// actions.getValue() at send time rather than from these deferred side effects.
				deferredOnChangeVersionRef.current += 1;
				deferredOnChangePayloadRef.current = null;
				if (deferredOnChangeFrameRef.current !== null) {
					cancelAnimationFrame(deferredOnChangeFrameRef.current);
					deferredOnChangeFrameRef.current = null;
				}
			};
		}, []);

		const flushEditorChangeSideEffects = useCallback(
			(payload: { textContent: string; promptHasSkill: boolean }, version?: number) => {
				stableEditorPropsLatestRef.current.editorApi?.core.actions.requestDocument((doc) => {
					if (version !== undefined && deferredOnChangeVersionRef.current !== version) {
						return;
					}

					const latest = stableEditorPropsLatestRef.current;
					const docNode = doc?.content.length ? normalizeConnectorSourceDoc(doc as DocNode) : null;

					if (!latest.isSelectingSuggestionRef.current) {
						if (!payload.promptHasSkill && latest.onAutoSuggestQueryChange) {
							latest.onAutoSuggestQueryChange(payload.textContent);
						} else {
							if (fg('aifc_autosuggest_link_processing')) {
								latest.setAutoSuggestADF(docNode);
							} else {
								latest.setAutoSuggestQuery(payload.textContent);
							}
						}
					}

					// When user deletes a prompt from the input,
					// only reset app filters and banner if they were set by a prompt card click
					if (docNode === null && latest.appFiltersSetByPromptRef.current) {
						latest.setChatMode(latest.promptDraftAndModeKey, { appFilters: undefined });
						latest.hideAppConnectionBanner();
					}

					if (latest.customSlotBeforeChatModeMenuRenderer) {
						latest.setLivePromptText(payload.textContent);
					}
					latest.onPromptChange?.(docNode);
					latest.setDraftMessage(docNode);

					latest.isSelectingSuggestionRef.current = false;
				});
			},
			[],
		);

		const scheduleDeferredEditorChange = useCallback(
			(payload: { textContent: string; promptHasSkill: boolean }) => {
				deferredOnChangeVersionRef.current += 1;
				const version = deferredOnChangeVersionRef.current;
				deferredOnChangePayloadRef.current = payload;

				if (deferredOnChangeFrameRef.current !== null) {
					cancelAnimationFrame(deferredOnChangeFrameRef.current);
				}

				deferredOnChangeFrameRef.current = requestAnimationFrame(() => {
					deferredOnChangeFrameRef.current = null;
					const currentPayload = deferredOnChangePayloadRef.current;

					if (!currentPayload || deferredOnChangeVersionRef.current !== version) {
						return;
					}

					flushEditorChangeSideEffects(currentPayload, version);
				});
			},
			[flushEditorChangeSideEffects],
		);

		const stableOnSave = useCallback(() => saveRef.current?.(), []);

		const handleEditorChange = useCallback(
			(ev: Parameters<NonNullable<EditorNextProps['onChange']>>[0]) => {
				const textContent = ev.state.doc.textContent;
				if (
					shouldApplySeededInitialPromptText &&
					shouldUseSeededDefaultValueRef.current &&
					textContent !== initialPromptText
				) {
					if (!hasSentWelcomePromptEditedAnalyticsRef.current) {
						sendWelcomeFromRovoChatPromptAnalytics('edited');
						hasSentWelcomePromptEditedAnalyticsRef.current = true;
					}
					disableSeededDefaultValue();
				}

				stableEditorPropsLatestRef.current.detectUnconnectedSource(textContent);
				setInputValueLength(getActualTextContentLength(ev.state.doc));
				let foundCodeBlock = false;
				let foundSkillExtension = false;
				const liveAgentMentions: Array<{
					mentionNodeId: string;
					mentionLocalId?: string;
				}> = [];
				ev.state.doc.descendants((node) => {
					if (node.type.name === 'codeBlock') {
						foundCodeBlock = true;
					}
					if (
						node.type.name === 'inlineExtension' &&
						node.attrs.extensionKey === ROVO_SKILL_COMBINED_EXTENSION_KEY
					) {
						foundSkillExtension = true;
					}
					const rawAgentMentionId = node.attrs?.id;
					const agentMentionId =
						typeof rawAgentMentionId === 'string' ? rawAgentMentionId.trim() : undefined;
					const isAgentMention =
						node.type.name === 'mention' &&
						node.attrs?.userType === 'APP' &&
						(node.attrs?.appType === undefined || node.attrs?.appType === 'agent');
					if (isAgentMention && agentMentionId) {
						const rawMentionLocalId = node.attrs?.localId;
						liveAgentMentions.push({
							mentionNodeId: agentMentionId,
							...(typeof rawMentionLocalId === 'string' && rawMentionLocalId
								? { mentionLocalId: rawMentionLocalId }
								: {}),
						});
					}
					return true;
				});
				setHasCodeBlock(foundCodeBlock);
				setPromptHasSkill(foundSkillExtension);
				reconcileAgentMentions(liveAgentMentions);

				const payload = {
					textContent,
					promptHasSkill: foundSkillExtension,
				};

				scheduleDeferredEditorChange(payload);
			},
			[
				disableSeededDefaultValue,
				initialPromptText,
				reconcileAgentMentions,
				scheduleDeferredEditorChange,
				sendWelcomeFromRovoChatPromptAnalytics,
				shouldApplySeededInitialPromptText,
			],
		);

		const stableOnChange: NonNullable<EditorNextProps['onChange']> = useCallback(
			(ev) => {
				handleEditorChange(ev);
			},
			[handleEditorChange],
		);

		const seededInitialPromptTextDocument = useMemo(
			() =>
				shouldApplySeededInitialPromptText && initialPromptText
					? createInitialPromptTextDocument(initialPromptText)
					: undefined,
			[initialPromptText, shouldApplySeededInitialPromptText],
		);
		const initialEditorDefaultValue = useMemo<EditorNextProps['defaultValue']>(
			() =>
				shouldApplySeededInitialPromptText && draftMessage
					? draftMessage
					: shouldApplySeededInitialPromptText
						? seededInitialPromptTextDocument
						: undefined,
			[draftMessage, seededInitialPromptTextDocument, shouldApplySeededInitialPromptText],
		);

		const handleSeededInitialPromptTextEditorReady = useCallback(
			({
				initialPromptText,
				shouldApplySeededInitialPromptText,
				draftMessage,
			}: {
				initialPromptText?: string;
				shouldApplySeededInitialPromptText: boolean;
				draftMessage?: DocNode | null;
			}): boolean => {
				if (
					!initialPromptText ||
					!shouldApplySeededInitialPromptText ||
					!FeatureGates.getExperimentValue('cc_ai_growth_welcome_from_rovo', 'isEnabled', false)
				) {
					return false;
				}

				if (draftMessage) {
					disableSeededDefaultValue();
					setInputValueLength(extractContent(draftMessage).processedText.length);
					return true;
				}

				if (shouldUseSeededDefaultValueRef.current) {
					setInputValueLength(initialPromptText.trim().length);
				}

				return true;
			},
			[disableSeededDefaultValue],
		);

		const stableOnEditorReady = useCallback((actions: EditorActions) => {
			const { initialPromptText, shouldApplySeededInitialPromptText, draftMessage } =
				stableEditorPropsLatestRef.current;
			if (
				handleSeededInitialPromptTextEditorReady({
					initialPromptText,
					shouldApplySeededInitialPromptText,
					draftMessage,
				})
			) {
				return;
			}

			applyInitialEditorDocument({
				actions,
				initialPromptText,
				shouldSeedInitialPromptText,
				draftMessage,
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps -- Keep stable so editor ready does not re-run when seeded prompt state changes.
		}, []);

		const sharedEditorProps = React.useMemo<
			Omit<EditorNextProps, 'onSave' | 'onChange' | 'onEditorReady'>
		>(() => {
			return {
				popupsMountPoint: editorPopupsMountPoint ?? undefined,
				popupsBoundariesElement: editorPopupsBoundariesElement,
				popupsScrollableElement: editorPopupsScrollableElement,
				assistiveLabel: assistiveLabel,
				minHeight:
					minHeight ??
					(contentContext === 'staging-area'
						? DEFAULT_INPUT_HEIGHT_STAGING_AREA
						: DEFAULT_INPUT_HEIGHT_ROVO_CHAT),
				shouldFocus: shouldFocusOnMount,
				preset: preset,
				mentionProvider: mentionResource,
				maxHeight: maxHeight ?? 400,
				appearance: 'chromeless',
				defaultValue: initialEditorDefaultValue,
			};
		}, [
			editorPopupsMountPoint,
			editorPopupsBoundariesElement,
			editorPopupsScrollableElement,
			assistiveLabel,
			minHeight,
			contentContext,
			shouldFocusOnMount,
			preset,
			mentionResource,
			maxHeight,
			initialEditorDefaultValue,
		]);

		const stableEditorProps: EditorNextProps = React.useMemo(() => {
			return {
				...sharedEditorProps,
				onSave: stableOnSave,
				onChange: stableOnChange,
				onEditorReady: stableOnEditorReady,
			};
		}, [sharedEditorProps, stableOnSave, stableOnChange, stableOnEditorReady]);

		const shouldRenderInputAgentHat = agentMentionsEnabled;

		// When the user removes the active context via the banner's "Remove
		// context" button, the banner unmounts and focus would otherwise be lost
		// (stranding keyboard / screen reader users on a detached element). Move
		// focus back to the prompt input so they can keep typing. This must be a
		// stable reference so it does not defeat `ContextBanner`'s prop memo.
		const handleContextRemoved = useCallback(() => {
			if (fg('rovo_chat_context_banner_focus_return')) {
				editorApi?.core.actions.focus();
			}
		}, [editorApi]);
		const isMentionAgentsSpotlightEnabled = fg('post-office_rovo-chat-mention-agents-spotlight');

		const shouldRenderContextBanner =
			!isInSpaceContext &&
			(useCurrentPageContext ||
				(showContextBannerForEntityContextOverride &&
					Boolean(aiFeatureContextOverride?.entityContext)));

		return (
			<Box
				onKeyDown={handleKeyDown}
				xcss={cx(
					!disableWrapperMarginStyles &&
						!(hasQueuedMessages && fg('rovo_chat_message_queue')) &&
						styles.wrapperMarginBlock,
					!disableWrapperMarginStyles && !blankObjectStyles && styles.wrapperMarginInline,
				)}
			>
				{isInSpaceContext && (
					<SpaceContextBanner
						sourceCount={spaceSourceCount}
						sources={spaceSources}
						activeContext={activePageContext}
						zeroSourcesAction={zeroSourcesAction}
					/>
				)}
				{shouldRenderContextBanner && (
					<UFOSegment name="context-banner">
						<ContextBanner
							contentContext={contentContext}
							currentPageAsContextStoreKey={promptDraftAndModeKey}
							enableBrowserContextBanner={enableBrowserContextBanner}
							browserContextSanitizer={browserContextSanitizer}
							aiFeatureContextOverride={aiFeatureContextOverride}
							onContextRemoved={handleContextRemoved}
						/>
					</UFOSegment>
				)}
				<AppConnectionBannerWrapper />
				{showConnectorTip && (
					<ConnectorTip unconnectedSourceTip={unconnectedSourceTip} dismissTip={dismissTip} />
				)}
				{isMicPermissionDenied && <DictationMicPermissionTip />}
				<SkillsSpotlightController enableSkills={enableSkills} onViewSkills={onDiscoverSkillsClick}>
					<MentionAgentsSpotlightController
						isEnabled={isMentionAgentsSpotlightEnabled}
						mode={mode}
						shouldRenderInputAgentHat={shouldRenderInputAgentHat}
						hasAvailableAgents={hasAvailableAgents}
						hasActiveAgentMention={hasActiveSpecialistAgentMention}
					>
						{({
							showAgentSourceControlSpotlight,
							onDismissAgentSourceControlSpotlight,
							sourceControlPostOfficeMessage,
						}) => (
							<ChatInputInner
								isRenderStabilizationEnabled={isRenderStabilizationEnabled}
								files={files}
								handleFilesSelected={handleFilesSelected}
								handleUrlsSelected={handleUrlsSelected}
								urlValidationConfig={urlValidationConfig}
								chatModeReasoningMode={chatModeState.reasoningMode}
								chatModeDeepResearchEnabled={chatModeState.deepResearchEnabled}
								chatModeTaskModeEnabled={chatModeState.taskModeEnabled}
								taskModeAnimationTrigger={taskModeAnimationTrigger}
								shouldKeepTaskModePromptMounted={agentMentionsEnabled}
								isBackgroundTransparent={isBackgroundTransparent}
								dropdownSlot={dropdownSlot}
								isAgentTyping={isAgentTyping}
								showSingleLineEditor={showSingleLineEditor}
								hoveredPrompt={hoveredPrompt}
								forceTruncatedWrapperOnEmptyEditor={forceTruncatedWrapperOnEmptyEditor}
								inputValueLength={inputValueLength}
								blankObjectStyles={blankObjectStyles}
								selectedAgentForInputHat={selectedAgentForInputHat}
								shouldRenderInputAgentHat={shouldRenderInputAgentHat}
								selectedAgentInputHatImageUrl={selectedAgentInputHatImageUrl}
								handleSelectedAgentDismiss={handleSelectedAgentDismiss}
								siteId={siteId}
								topAccessory={topAccessory}
								shouldShowDeepResearchInAddMenu={shouldShowDeepResearchInAddMenu}
								isSpecialistPromptInput={isSpecialistPromptInput}
								canSwitchBackToRovo={canSwitchBackToRovo}
								setDeepResearchEnabled={setDeepResearchEnabled}
								promptDraftAndModeKey={promptDraftAndModeKey}
								onReasoningModeChange={onReasoningModeChange}
								isDisabled={isDisabled}
								handleEditorBlur={handleEditorBlur}
								hasCodeBlock={hasCodeBlock}
								contentContext={contentContext}
								editorProps={stableEditorProps}
								editorApi={editorApi}
								promptHasSkill={promptHasSkill}
								enableSkills={enableSkills}
								enableSkillsInStagingArea={enableSkillsInStagingArea}
								enableSkillsInSpecialistPromptInput={enableSkillsInSpecialistPromptInput}
								charactersOverLimit={charactersOverLimit}
								customSlotAboveActionRow={customSlotAboveActionRow}
								showChatModeMenu={showChatModeMenu}
								showActionsButton={showActionsButton}
								actionsMenuItemsVisibility={boundaryActionsMenuItemsVisibility}
								entryPoint={entryPoint}
								mode={mode}
								menuPosition={menuPosition}
								openLinkTypeAheadFromMenu={openLinkTypeAheadFromMenu}
								openMentionTypeAhead={openMentionTypeAhead}
								openAddTypeAhead={openAddTypeAhead}
								onDiscoverSkillsClick={onDiscoverSkillsClick}
								handleDeepResearchClick={handleDeepResearchClick}
								onBrowsePromptLibraryClick={onBrowsePromptLibraryClick}
								onCreateSpaceClick={onCreateSpaceClick}
								hasActiveAgentMention={hasActiveSpecialistAgentMention}
								assistanceServiceParams={boundaryAssistanceServiceParams}
								fileUploadAssistanceOverrides={fileUploadAssistanceOverrides}
								contextProviders={contextProviders}
								onContextBrowserShow={onContextBrowserShow}
								onContextAvailable={onContextAvailable}
								useCurrentPageContext={useCurrentPageContext}
								closeTypeAhead={closeTypeAhead}
								closeEditorToolbar={closeEditorToolbar}
								shouldRenderQuickActionsInPortal={shouldRenderQuickActionsInPortal}
								enableBrowserContextBanner={enableBrowserContextBanner}
								browserContextSanitizer={browserContextSanitizer}
								shouldRenderChatModeMenusInPortal={shouldRenderChatModeMenusInPortal}
								webSearchConfigEnabled={webSearchConfigEnabled}
								setWebSearchEnabled={setWebSearchEnabled}
								onWebSearchToggle={onWebSearchToggle}
								onOrgKnowledgeToggle={onOrgKnowledgeToggle}
								onFilteredAppsChange={onFilteredAppsChange}
								onInsertTranscript={handleInsertTranscript}
								onMicPermissionDenied={handleMicPermissionDenied}
								setReasoningMode={setReasoningMode}
								renderAutoApplyToggle={renderAutoApplyToggle}
								enableAppsDiscoveryUi={enableAppsDiscoveryUi}
								enableMaxMode={enableMaxMode}
								setTaskModeAnimationTrigger={setTaskModeAnimationTrigger}
								onReasoningModeMenuOpenChange={handleReasoningModeMenuOpenChange}
								isReasoningModeDisabledByDeepResearch={isReasoningModeDisabledByDeepResearch}
								stableFileInputChange
								formatMessage={formatMessage}
								customSlotBeforeSubmitButton={customSlotBeforeSubmitButton}
								submitButtonOnSave={stableOnSave}
								SubmitButton={SubmitButton}
								isSendDisabled={isSendDisabled}
								submitButtonTooltip={submitButtonTooltip}
								tooltipOptions={tooltipOptions}
								onAbort={onAbort}
								getChatMode={getChatMode}
								shouldRenderVoiceEnabledFooterRow={shouldRenderVoiceEnabledFooterRow}
								productKey={productKey}
								autoSuggestConfig={autoSuggestConfig}
								autoSuggestQuery={autoSuggestQuery}
								autoSuggestADF={autoSuggestADF}
								handleSuggestionSelect={handleSuggestionSelect}
								pageContextType={pageContextType}
								customSlotBeforeChatModeMenu={
									typeof customSlotBeforeChatModeMenu === 'function'
										? customSlotBeforeChatModeMenu({ prompt: livePromptText })
										: customSlotBeforeChatModeMenu
								}
								showAgentSourceControlSpotlight={showAgentSourceControlSpotlight}
								onDismissAgentSourceControlSpotlight={onDismissAgentSourceControlSpotlight}
								dictationOverlayBackground={dictationOverlayBackground}
								sourceControlPostOfficeMessage={sourceControlPostOfficeMessage}
								showSourceModeToggle={fg('rovo_chat_space_source_mode') && isInSpaceContext}
							/>
						)}
					</MentionAgentsSpotlightController>
				</SkillsSpotlightController>
				{dropdownSlot && (
					<Box xcss={styles.dropdownSlotAnchor}>
						<Box xcss={styles.dropdownSlotOverlay}>{dropdownSlot}</Box>
					</Box>
				)}
			</Box>
		);
	},
);

export const RovoChatPromptInput: React.ForwardRefExoticComponent<
	ChatPromptInputProps & React.RefAttributes<ChatPromptInputRef>
> = forwardRef<ChatPromptInputRef, ChatPromptInputProps>(
	({ outerBottomAccessory, ...props }, ref) => {
		// Force an opaque white prompt background when outerBottomAccessory is present so it
		// covers the overlap area, leaving only the border-radius corner gaps visible.
		const isBackgroundTransparent = outerBottomAccessory ? false : props.isBackgroundTransparent;

		return (
			<Box xcss={cx(props.shouldBeCentered && styles.fsCentered, styles.relativeWrapper)}>
				<Box xcss={outerBottomAccessory ? styles.promptInputLayerAboveBottomAccessory : undefined}>
					<EditorContext>
						<WithEditorActions
							render={(actions) => (
								<ChatInputInternal
									{...props}
									isBackgroundTransparent={isBackgroundTransparent}
									actions={actions}
									ref={ref}
								/>
							)}
						/>
					</EditorContext>
				</Box>
				{outerBottomAccessory && (
					<Box xcss={styles.outerBottomAccessory} testId="chat-input-bottom-accessory">
						{outerBottomAccessory}
					</Box>
				)}
			</Box>
		);
	},
);
